import React, { useEffect, useRef, useState } from 'react';
import { Button, Divider, Flex, Input, message } from 'antd';
import { DeleteOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons';
import { FEEDBACK_ERP_ERROR_NAME, useMessageStore } from '../../stores/message.store';
import './ChatPage.scss';
import ChatMessage from '../../components/chat/ChatMessage';
import useAuthStore from '../../stores/auth.store';
import { ChatbotSatisfaction } from '../../models/chatbot.entity';

const ChatPage = () => {
  const [inputText, setInputText] = useState<string>('');
  const [firstMessage, setFirstMessage] = useState<boolean>(false);
  const [submittedFeedback, setSubmittedFeedback] = useState<ChatbotSatisfaction | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState<ChatbotSatisfaction | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const messages = useMessageStore((state) => state.messages);
  const sendMessage = useMessageStore((state) => state.sendMessage);
  const initSession = useMessageStore((state) => state.initSession);
  const deleteSession = useMessageStore((state) => state.deleteSession);
  const submitFeedback = useMessageStore((state) => state.submitFeedback);

  const userUuid = useAuthStore((state) => state.getUserUuid());

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  //When message is sent scroll bar goes to the bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  const sendTextMessage = async () => {
    const inputTextCopy = inputText;
    setInputText('');
    messageApi.open({
      type: 'loading',
      content: 'Esperando respuesta..',
      duration: 0,
    });

    try {
      if (!firstMessage) {
        await initSession(inputTextCopy, userUuid);
        setFirstMessage(true);
      } else {
        await sendMessage(inputTextCopy, userUuid);
      }
    } catch {
      messageApi.destroy();
      messageApi.open({
        type: 'error',
        content: 'Algo ha fallado. Vuelve a intentarlo más tarde.',
        duration: 10,
      });
      return;
    }

    messageApi.destroy();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendTextMessage();
    }
  };

  const giveFeedback = async (satisfaction: ChatbotSatisfaction) => {
    if (submittingFeedback || submittedFeedback) return;

    setSubmittingFeedback(satisfaction);
    try {
      await submitFeedback(userUuid, satisfaction);
      setSubmittedFeedback(satisfaction);
      messageApi.success('Feedback enviado.');
    } catch (error) {
      if (error instanceof Error && error.name === FEEDBACK_ERP_ERROR_NAME) {
        messageApi.error('El feedback se ha recibido, pero no se ha podido crear la incidencia en ERP.');
      } else {
        messageApi.error('No se ha podido procesar el feedback. Vuelve a intentarlo más tarde.');
      }
    } finally {
      setSubmittingFeedback(null);
    }
  };

  const deleteCurrentSession = () => {
    deleteSession();
    setFirstMessage(false);
    setSubmittedFeedback(null);
  };

  return (
    <Flex className={'ChatPage'} vertical>
      {contextHolder}
      <div className={'ChatPage__MessagesContainer'} ref={messagesEndRef}>
        {messages.map((message, index) => (
          <ChatMessage key={index} text={message.text} sender={message.sender} />
        ))}
      </div>
      <Divider className={'ChatPage__Divider'} />
      <div className={'ChatPage__InputSection'}>
        {firstMessage && (
          <div className={'ChatPage__InputSection__Feedback'}>
            <Button
              color="default"
              variant="text"
              onClick={() => giveFeedback('positive')}
              disabled={submittingFeedback !== null || submittedFeedback !== null}
              loading={submittingFeedback === 'positive'}
              aria-label="Enviar feedback positivo"
              aria-pressed={submittedFeedback === 'positive'}
              className={'ChatPage__InputSection__Feedback__Button'}
            >
              <LikeOutlined
                className={`ChatPage__InputSection__Feedback__Button__Icon__Like${
                  submittedFeedback === 'positive'
                    ? ' ChatPage__InputSection__Feedback__Button__Icon__Like--selected'
                    : ''
                }`}
              />
            </Button>
            <Button
              color="default"
              variant="text"
              onClick={() => giveFeedback('negative')}
              disabled={submittingFeedback !== null || submittedFeedback !== null}
              loading={submittingFeedback === 'negative'}
              aria-label="Enviar feedback negativo"
              aria-pressed={submittedFeedback === 'negative'}
              className={'ChatPage__InputSection__Feedback__Button'}
            >
              <DislikeOutlined
                className={`ChatPage__InputSection__Feedback__Button__Icon__Dislike${
                  submittedFeedback === 'negative'
                    ? ' ChatPage__InputSection__Feedback__Button__Icon__Dislike--selected'
                    : ''
                }`}
              />
            </Button>
          </div>
        )}
        <Button icon={<DeleteOutlined />} danger onClick={deleteCurrentSession} className={'ChatPage__Delete'} />
        <Input
          className={'ChatPage__InputSection__Input'}
          placeholder="Escribe tu mensaje..."
          type="text"
          value={inputText}
          size={'large'}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          allowClear
        />
        <Button type="primary" className={'ChatPage__InputSection__Button'} onClick={sendTextMessage}>
          Enviar
        </Button>
      </div>
    </Flex>
  );
};

export default ChatPage;
